/**
 * Drag space for a finger drag on the plan.
 *
 * A touch drag is replayed to the mouse path as synthetic mouse events, and
 * two screen offsets sit between the fingertip and the position those events
 * carry:
 *
 * - the PROMOTION offset — a drag only arms once the finger has travelled
 *   TOUCH_DRAG_THRESHOLD px, and the mousedown must land back where the
 *   finger STARTED (that is the point whose hit-test claimed the finger);
 *   so every later position is pulled back by the same delta.
 * - the LIFT — an object dragged by its BODY rides above the fingertip so
 *   the finger does not cover what it moves.
 *
 * Both are CONSTANT for the life of a drag. That is the invariant this
 * module exists to hold, and it has been broken twice in the same place:
 * every drag path downstream is DELTA-based, so anything that changes here
 * mid-drag lands in the drag delta and moves the object on its own.
 *
 *  1. A lift sized by the finger's TRAVEL is a −Y offset that grows as you
 *     drag, so a sideways drag climbed 56 px, a downward one stood still
 *     until it had out-run the ramp, and dragging back walked the object
 *     down again.
 *  2. A lift that ramps over TIME fixes the direction coupling but is still
 *     motion the finger did not ask for: for the ~180 ms of the ramp the
 *     object rose at its own pace, which out-runs a slow downward drag — so
 *     it set off UPWARD, the opposite way, before turning to follow.
 *
 * So there is no ramp. The lift is applied whole from the first dispatched
 * frame of a drag that has one, and not at all to a drag that does not.
 */

/** Screen pixels a lifted object sits above the fingertip. */
export const TOUCH_LIFT_PX = 56;

/**
 * Map a live touch point into the drag space the synthetic mouse events use.
 *
 * @param {{clientX: number, clientY: number, screenX?: number, screenY?: number}} point
 * @param {Object} [state]
 * @param {{x: number, y: number}|null} [state.dragOffset] - promotion offset
 * @param {boolean} [state.lift] - does this drag ride above the finger?
 * @returns {{clientX: number, clientY: number, screenX: number, screenY: number}}
 */
export const toDragSpace = (point, state = {}) => {
  const { dragOffset = null, lift = false } = state;
  const dx = dragOffset ? dragOffset.x : 0;
  const dy = dragOffset ? dragOffset.y : 0;
  const up = lift ? TOUCH_LIFT_PX : 0;
  return {
    clientX: point.clientX - dx,
    clientY: point.clientY - dy - up,
    screenX: (point.screenX ?? point.clientX) - dx,
    screenY: (point.screenY ?? point.clientY) - dy - up,
  };
};
